/**
 * The reveal.
 *
 * The result is already decided and already booked by the time this mounts —
 * the server rolled it, the cards are in the vault, the balance has moved.
 * Nothing here can change the outcome, which is why closing the tab mid-reveal
 * is safe. This only controls the pacing of finding out.
 *
 * The staging is ported from the TradingCard rip, and the load-bearing trick is
 * the handoff in `slideOut`: the card slab that travels out of the pack is real
 * geometry inside the model, so the depth buffer occludes it correctly against
 * the pack mouth. But it only carries the card *back* — the face is live DOM,
 * which cannot be rendered into WebGL. So once the slab is clear of the mouth
 * we snap the DOM card onto the slab's exact projected rect and let it
 * transition from there. The seam is invisible because both objects are in the
 * same place at the same moment.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import * as pack3d from '../lib/pack3d'
import type { OpenResult, PackSummary } from '../lib/api'
import { multiple, shortHash, usd } from '../lib/format'
import { CardBack, CardFace } from './CardFace'
import { RarityChip, TIER_ACCENT } from './ui'

type Stage = 'sealed' | 'tearing' | 'reveal' | 'settled'

/** Horizontal drag, in px, that completes the tear. */
const TEAR_DISTANCE = 96
/** Drag distance mapped to full peel, so the lid tracks the finger. */
const TEAR_SCALE = 140
/** The gap the original leaves between the tear firing and the first card. */
const TEAR_TO_REVEAL_MS = 720

export function RipTheatre({
  pack,
  result,
  onSellAll,
  onClose,
  selling,
}: {
  pack: PackSummary
  result: OpenResult
  onSellAll: () => void
  onClose: () => void
  selling?: boolean
}) {
  const open = result.open
  const pulls = open.pulls

  const packRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const timers = useRef<number[]>([])

  const [ready, setReady] = useState(false)
  const [stage, setStage] = useState<Stage>('sealed')
  const [index, setIndex] = useState(0)
  const [flipped, setFlipped] = useState<boolean[]>(() => pulls.map(() => false))
  /* True only while the 3D slab is physically travelling out of the pack.
     The pack has to sit in front of the deck for that stretch, so the mouth
     occludes the card properly — and behind it the rest of the time, or the
     revealed face is hidden by the very pack it came out of. */
  const [travelling, setTravelling] = useState(false)
  /* Which cards have left the pack. This is React state rather than a class
     toggled on the node, because React owns `className` — any re-render (a
     flip, the running total) would put `is-tucked` straight back and the card
     would vanish mid-reveal. */
  const [released, setReleased] = useState<boolean[]>(() => pulls.map(() => false))

  const accent = TIER_ACCENT[pack.tier]

  const after = useCallback((ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms))
  }, [])

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout)
      timers.current = []
    },
    [],
  )

  /* Hold the page still underneath.
     The theatre is `fixed`, so without this the wheel falls through to the body
     and the shop scrolls away behind a modal nobody can scroll — which reads as
     the page having broken. Restored on unmount rather than set to a constant,
     so an inline overflow someone set elsewhere survives a rip. */
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  /* ---- mount the 3D pack ---- */
  useEffect(() => {
    const host = packRef.current
    if (!host) return
    let cancelled = false

    pack3d
      // No idle spin here: the tear tab is a moving target on a rotating pack,
      // and this is the one screen where hitting it is the whole interaction.
      // The pack still turns freely when dragged.
      .mount(host, { accent, tier: pack.tier, art: pack.art, tearable: true, idle: false })
      .then((ok) => {
        if (!cancelled) setReady(ok)
      })
      .catch(() => setReady(false))

    return () => {
      cancelled = true
      pack3d.unmount(host)
    }
  }, [accent, pack.art, pack.tier])

  /* ---- the tear gesture ----
     Two gestures share this pack: drag the tab to rip it, drag anywhere else
     to spin it. Which one you get is decided on pointerdown by asking pack3d
     whether the pointer is on the tear Empty — so the answer moves with the
     pack as it rotates. */
  const tear = useCallback(() => {
    const host = packRef.current
    if (!host) return

    setStage((current) => {
      if (current !== 'sealed') return current

      navigator.vibrate?.([12, 30, 60])
      pack3d.tearOff(host)
      // Start the pack settling back and down into its reveal position now,
      // while the lid is still in the air, so it has arrived by the time the
      // first card comes out.
      after(140, () => pack3d.setFrame(host, 'reveal', 560))
      after(TEAR_TO_REVEAL_MS, () => {
        pack3d.setTearable(host, false)
        pack3d.setFrame(host, 'reveal', 0)
        setStage('reveal')
      })
      return 'tearing'
    })
  }, [after])

  useEffect(() => {
    const host = packRef.current
    if (!host || !ready || stage !== 'sealed') return

    const gated = pack3d.hasTearTab()
    const onTab = (x: number, y: number) => !gated || pack3d.hitTearTab(host, x, y)

    let startX: number | null = null
    let fired = false

    const down = (e: PointerEvent) => {
      if (!onTab(e.clientX, e.clientY)) {
        startX = null // a rotate, not a rip
        return
      }
      startX = e.clientX
      fired = false
    }

    const move = (e: PointerEvent) => {
      if (startX === null || fired) return
      const dx = Math.abs(e.clientX - startX)
      pack3d.setTear(host, Math.min(1, dx / TEAR_SCALE))
      if (dx > TEAR_DISTANCE) {
        fired = true
        tear()
      }
    }

    /* Bound to `pointercancel` as well as `pointerup`. The well sets
       `touch-action: pan-y`, so a tear drag that strays vertical is handed to
       the browser as a scroll and the page is sent a cancel instead of an up.
       Listening for only the up leaves `startX` set and the lid frozen at
       whatever peel the finger got to, with no way back. */
    const up = () => {
      if (!fired) pack3d.setTear(host, 0) // let go early, lid settles back
      startX = null
    }

    const click = (e: MouseEvent) => {
      if (!fired && onTab(e.clientX, e.clientY)) {
        fired = true
        tear()
      }
    }

    host.addEventListener('pointerdown', down)
    host.addEventListener('click', click)
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      host.removeEventListener('pointerdown', down)
      host.removeEventListener('click', click)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [ready, stage, tear])

  /* ---- pulling one card out ----
     The 3D slab travels first; the DOM card takes over from its projected
     rect once the slab is clear of the mouth. */
  const slideOut = useCallback(
    (i: number) => {
      const host = packRef.current
      const release = () =>
        setReleased((current) => current.map((value, n) => (n === i ? true : value)))

      if (!host || !ready || !pack3d.hasCard()) {
        release()
        return
      }

      setTravelling(true)
      const duration = pack3d.slideCardOut(host)

      after(duration, () => {
        // Measured before the release, while the slab is still posed where it
        // finished — this is the rect the DOM card has to start from.
        const from = pack3d.cardRect(host)
        release()

        // One frame later the card has its untucked box, so the inverted
        // transform can be computed and then played out.
        requestAnimationFrame(() => {
          const el = cardRefs.current[i]
          if (el && from) {
            const to = el.getBoundingClientRect()
            if (to.width && to.height) {
              const scale = (from.width / to.width + from.height / to.height) / 2
              const dx = from.left + from.width / 2 - (to.left + to.width / 2)
              const dy = from.top + from.height / 2 - (to.top + to.height / 2)
              el.style.transition = 'none'
              el.style.transform = `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px) scale(${scale.toFixed(4)})`
              void el.offsetWidth // force the browser to take the start state
              el.style.transition = ''
              el.style.transform = ''
            }
          }
          pack3d.hideCard(host)
          setTravelling(false)
          navigator.vibrate?.(10)
        })
      })
    },
    [after, ready],
  )

  useEffect(() => {
    if (stage === 'reveal') slideOut(index)
  }, [index, slideOut, stage])

  const flip = useCallback((i: number) => {
    setFlipped((current) => current.map((value, n) => (n === i ? true : value)))
    navigator.vibrate?.(8)
  }, [])

  const next = useCallback(() => {
    setIndex((i) => {
      if (i + 1 >= pulls.length) {
        setStage('settled')
        return i
      }
      return i + 1
    })
  }, [pulls.length])

  const revealAll = useCallback(() => {
    const host = packRef.current
    if (host) pack3d.hideCard(host)
    timers.current.forEach(clearTimeout)
    timers.current = []
    setTravelling(false)
    setReleased(pulls.map(() => true))
    setFlipped(pulls.map(() => true))
    setIndex(pulls.length - 1)
    setStage('settled')
  }, [pulls])

  const advance = useCallback(() => {
    if (stage !== 'reveal') return
    if (!flipped[index]) flip(index)
    else next()
  }, [flip, flipped, index, next, stage])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && (stage === 'settled' || stage === 'sealed')) onClose()
      if (event.key === 'Enter' && stage === 'sealed') tear()
      if (event.key === 'Enter' && stage === 'reveal') advance()
      if (event.key === ' ') {
        event.preventDefault()
        if (stage === 'reveal') advance()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [advance, onClose, stage, tear])

  const revealedTotal = useMemo(
    () =>
      pulls.reduce((sum, pull, i) => (flipped[i] ? sum + pull.value : sum), 0),
    [flipped, pulls],
  )

  const profit = open.total - open.price

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Opening a ${pack.name}`}
      className="fixed inset-0 z-50 overflow-y-auto overscroll-contain"
      style={{ background: 'rgb(0 0 0 / 0.95)', backdropFilter: 'blur(10px)' }}
    >
      {/* `min-h-dvh` and a growing middle row, so the pack is centred in the
          viewport rather than pinned under the header with dead space beneath
          it. The width cap sits on each row instead of the column, which lets
          the stage go wider than the text furniture around it. */}
      <div className="flex min-h-dvh w-full flex-col px-4 py-4 sm:px-6 lg:px-8">
        <header className="mx-auto flex w-full max-w-6xl shrink-0 items-center justify-between gap-4 border-b pb-4">
          <div className="min-w-0">
            <div className="eyebrow">
              {stage === 'reveal' ? (
                <>
                  Card {index + 1} / {pulls.length}
                </>
              ) : (
                'Opening'
              )}
            </div>
            <h2 className="mt-1 truncate text-lg font-semibold tracking-tight">
              {pack.name} · {usd(pack.price, { whole: true })}
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {stage === 'reveal' && (
              <button className="btn btn-ghost btn-sm" onClick={revealAll}>
                Reveal all
              </button>
            )}
            <button
              className="btn btn-quiet btn-sm"
              onClick={onClose}
              disabled={stage === 'tearing' || stage === 'reveal'}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </header>

        {/* Takes every pixel the header and footer do not. While a pack is on
            screen its contents are centred; once the cards are laid out they
            run from the top, because a settled grid taller than the viewport
            should scroll from its first row rather than from its middle. */}
        <div
          className={`mx-auto flex w-full flex-1 flex-col ${
            stage === 'settled' ? 'max-w-6xl' : 'max-w-5xl'
          }`}
          /* `safe center` rather than plain centring: a three-card grid should
             sit in the middle of the stage, but a ten-card one is taller than
             the viewport, and ordinary `center` would push its first row off
             the top edge where nothing can scroll it back. `safe` falls back to
             start-alignment exactly when that would happen. */
          style={{ justifyContent: 'safe center' }}
        >
          <div className="rip-well" data-stage={stage}>
          {stage !== 'settled' && (
            <div
              ref={packRef}
              className="rip-pack"
              data-spent={stage === 'reveal' ? '' : undefined}
              data-receded={stage === 'reveal' && !travelling ? '' : undefined}
              style={{ ['--acc' as string]: accent }}
            >
              {!ready && <CssPackFallback pack={pack} accent={accent} onTear={tear} />}
            </div>
          )}

          {stage === 'reveal' && (
            <div className="rip-deck" style={{ zIndex: travelling ? 2 : 5 }}>
              {pulls.map((pull, i) => {
                const done = i < index
                const active = i === index
                /* 1 is the card just dealt. Drives how far a spent card is
                   pushed off to the side and how faint it goes, so the discard
                   fans out instead of piling up on one spot — six cards at a
                   flat 0.28 stack up to nearly opaque. */
                const depth = index - i

                return (
                  <div
                    key={`${pull.slotIndex}-${pull.asset.symbol}`}
                    ref={(el) => {
                      cardRefs.current[i] = el
                    }}
                    className={`rip-card ${released[i] ? '' : 'is-tucked'} ${active ? 'is-active' : ''} ${
                      flipped[i] ? 'is-flipped' : ''
                    } ${done ? 'is-done' : ''}`}
                    style={{
                      /* Depends on state, not just position. Descending by
                         index is right while the deck is stacked in the pack —
                         card 0 is the top of the pile — but a dealt card has to
                         drop behind the one being read, or it paints its own
                         translucent ghost across it. */
                      zIndex: done ? Math.max(1, 20 - depth) : active ? 100 : 50 - i,
                      ...(done ? ({ '--depth': depth } as React.CSSProperties) : {}),
                    }}
                    onClick={() => active && advance()}
                  >
                  <div className="rip-card__inner">
                    <div className="rip-card__side">
                      {/* The same component the vault and the dashboard use, so
                          the face the 3D slab hands over to is drawn by one
                          piece of code rather than by a near-copy of it. */}
                      <CardBack />
                    </div>
                    <div className="rip-card__side rip-card__back">
                      <CardFace pull={pull} landed={flipped[i]} />
                    </div>
                  </div>
                    {pull.slot === 'Hit' && !flipped[i] && (
                      <span className="rip-card__badge">Hit slot</span>
                    )}
                  </div>
                )
              })}
            </div>
          )}

            {stage === 'settled' && (
              // auto-fit rather than a breakpoint ladder of fixed grid-cols: a
              // fixed column count always creates that many tracks, so a
              // three-card pull in a five-column row left-aligns into the
              // first three cells and leaves the rest empty. auto-fit collapses
              // tracks with nothing in them, so the row is only ever as wide as
              // the cards actually in it — which is what `justify-center` needs
              // to have something to centre.
              <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(150px,220px))] justify-center gap-3 py-4">
                {pulls.map((pull) => (
                  <div
                    key={`${pull.slotIndex}-${pull.asset.symbol}`}
                    className="rise aspect-[3/4.1]"
                  >
                    <CardFace pull={pull} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {stage === 'sealed' && ready && (
          <p className="mx-auto max-w-sm shrink-0 pb-2 text-center text-xs" style={{ color: 'var(--color-faint)' }}>
            Drag the tab to rip it open, or drag anywhere else to turn the pack. The cards are already
            decided by the committed seed; this only shows you what it rolled.
          </p>
        )}

        {stage === 'reveal' && (
          <div className="mx-auto flex w-full max-w-5xl shrink-0 items-center justify-between gap-4 pb-2">
            <span className="text-xs" style={{ color: 'var(--color-faint)' }}>
              {flipped[index] ? 'Tap for the next card' : 'Tap the card to flip it'}
            </span>
            <span className="tnum text-sm font-semibold">{usd(revealedTotal)}</span>
          </div>
        )}

        {stage === 'settled' && (
          <footer className="rise panel mx-auto mt-4 w-full max-w-6xl shrink-0 p-5">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <div className="eyebrow">Pack value</div>
                <div className="mt-1.5 flex flex-wrap items-baseline gap-3">
                  <span className="tnum text-3xl font-bold">{usd(open.total)}</span>
                  <span
                    className="tnum text-sm font-semibold"
                    style={{ color: profit >= 0 ? 'var(--color-up)' : 'var(--color-down)' }}
                  >
                    {profit >= 0 ? '+' : '−'}
                    {usd(Math.abs(profit))} · {multiple(open.multiple)}
                  </span>
                </div>
                <div
                  className="mt-2 flex flex-wrap items-center gap-2 text-xs"
                  style={{ color: 'var(--color-muted)' }}
                >
                  Best pull
                  <RarityChip rarity={open.best} />
                  <span style={{ color: 'var(--color-faint)' }}>
                    · nonce {open.fairness.nonce} · commit{' '}
                    {shortHash(open.fairness.serverSeedHash, 6)}
                  </span>
                </div>
                <p className="mt-2 text-xs" style={{ color: 'var(--color-faint)' }}>
                  Already credited to your vault. Selling is optional.
                </p>
              </div>

              <div className="flex flex-wrap gap-2.5">
                <button className="btn btn-ghost" onClick={onClose}>
                  Keep {pulls.length} in vault
                </button>
                <button className="btn btn-primary" onClick={onSellAll} disabled={selling}>
                  {selling ? 'Selling…' : `Sell all for ${usd(open.total)}`}
                </button>
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  )
}

/** Shown when WebGL or the model is unavailable, so the pack still opens. */
function CssPackFallback({
  pack,
  accent,
  onTear,
}: {
  pack: PackSummary
  accent: string
  onTear: () => void
}) {
  return (
    <div className="grid size-full place-items-center">
      <button
        onClick={onTear}
        className="w-[210px] cursor-pointer"
        aria-label={`Tear open the ${pack.name}`}
      >
        <div
          className="h-6 border border-b-0"
          style={{ background: `linear-gradient(180deg, ${accent}3a, ${accent}14)`, borderColor: `${accent}55` }}
        />
        <div
          className="grid aspect-[3/4.2] place-items-center border border-t-0"
          style={{
            borderColor: `${accent}44`,
            background: `radial-gradient(130% 80% at 50% 0%, ${accent}2e 0%, transparent 60%), var(--color-surface-2)`,
          }}
        >
          <span className="btn btn-primary btn-sm">Tear open</span>
        </div>
      </button>
    </div>
  )
}
